import { use, useCallback, useMemo } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useData } from 'global/hooks/useData';
import sendData from 'redux/actions/sendData';

const parseJson = (val, fallback = {}) => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') return val;
  if (typeof val !== 'string') return fallback;
  try { return JSON.parse(val); } catch { return fallback; }
};

// json-api-normalizer recursively camelCases all keys when a value is an object,
// but when a value is a JSON string, parseJson preserves the original snake_case.
// Recursively convert all object keys back to snake_case so downstream code is consistent.
const toSnake = (s) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const snakeKeys = (obj) => {
  if (Array.isArray(obj)) return obj.map(snakeKeys);
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const k of Object.keys(obj)) {
    const val = obj[k];
    out[toSnake(k)] = (val && typeof val === 'object') ? snakeKeys(val) : val;
  }
  return out;
};

const normalizeTaskType = (tt) => {
  const relReqs = snakeKeys(parseJson(tt.relationRequirements, {}));
  const fieldReqs = snakeKeys(parseJson(tt.fieldRequirements, []));
  // field_requirements can be an array (legacy) or { fields, field_order }
  const customFields = Array.isArray(fieldReqs) ? fieldReqs : (fieldReqs.fields || []);
  const fieldOrder = Array.isArray(fieldReqs) ? null : (fieldReqs.field_order || null);
  const defaultFieldOverrides = Array.isArray(fieldReqs) ? null : (fieldReqs.default_field_overrides || null);
  const rawCloseout = snakeKeys(parseJson(tt.statusRequirements, {
    notes_required: false,
    photos_required: false,
    signature_required: false,
  }));
  return {
    id: tt.id,
    type: 'task-types',
    name: tt.name || tt.handle || '',
    handle: tt.handle || '',
    is_active: !!tt.enabled,
    location_types: Array.isArray(tt.realmTiers) ? tt.realmTiers : parseJson(tt.realmTiers, []),
    custom_name: tt.name || '',
    multi_select_behavior: (!Array.isArray(fieldReqs) && fieldReqs.multi_select_behavior) || tt.multiSelectBehavior || 'all_at_once',
    closeout_requirements: {
      notes_required: rawCloseout.notes_required ?? false,
      photos_required: rawCloseout.photos_required ?? false,
      signature_required: rawCloseout.signature_required ?? false,
      status_change_email: rawCloseout.status_change_email || 'send_by_default',
    },
    custom_fields: customFields,
    field_order: fieldOrder,
    default_field_overrides: defaultFieldOverrides,
    relation_requirements: relReqs,
    _taskTypeDefaultId: tt.taskTypeDefaultId,
    _appModeId: tt.appModeId,
    _realmId: tt.realmId,
  };
};

const toApiAttributes = (updates) => {
  const attrs = {};
  if (updates.custom_name !== undefined) attrs.name = updates.custom_name;
  if (updates.name !== undefined && !updates.custom_name) attrs.name = updates.name;
  if (updates.location_types !== undefined) attrs.realm_tiers = updates.location_types;
  if (updates.multi_select_behavior !== undefined) attrs.multi_select_behavior = updates.multi_select_behavior;
  if (updates.closeout_requirements !== undefined) {
    attrs.status_requirements = updates.closeout_requirements;
  }
  if (updates.custom_fields !== undefined) {
    attrs.field_requirements = {
      fields: updates.custom_fields,
      field_order: updates.field_order || null,
      default_field_overrides: updates.default_field_overrides || null,
      multi_select_behavior: updates.multi_select_behavior || null,
    };
  }
  if (updates.enabled !== undefined) attrs.enabled = updates.enabled;
  return attrs;
};

export const useTaskTypes = () => {
  const { destroyCache } = useData();

  const appModes = useRetrieveData(
    { url: '/app-modes' },
    {
      build: ['appModes'],
      useCache: {
        reload: 20
      }
    }
  );

  const taskTypes = useRetrieveData(
    { url: '/task-types' },
    {
      build: ['taskTypes'],
      params: {},
      transformData: (data) => (data || []).map(normalizeTaskType),
      useCache: {
        reload: 20
      }
    }
  );

  const accountSettings = useRetrieveData(
    { url: '/account-settings' },
    {
      build: ['accountSettings'],
      useCache: {
        reload: 20
      }
    }
  );

  const enabledModeIds = useMemo(() => {
    if (!accountSettings.data) return [1]; // default Quality
    // Use the last "App Mode" entry (realm override takes precedence over base)
    const setting = [...accountSettings.data].reverse().find((s) => s.name === 'App Mode');
    const val = setting ? parseJson(setting.value, [1]) : [1];
    return (Array.isArray(val) ? val : [1]).map(Number);
  }, [accountSettings.data]);

  const data = useMemo(() => {
    if (!taskTypes.data) return [];
    const modeMap = {};
    (appModes.data || []).forEach((m) => { modeMap[String(m.id)] = m.name || ''; });
    return taskTypes.data.map((tt) => ({
      ...tt,
      mode: modeMap[String(tt._appModeId)] || '',
    }));
  }, [taskTypes.data, appModes.data]);

  const updateTaskType = useCallback(async (id, updates) => {
    // Optimistic update so the UI reflects changes immediately
    if (taskTypes.data) {
      const optimistic = taskTypes.data.map((tt) =>
        tt.id === id ? { ...tt, ...(updates.enabled !== undefined ? { is_active: updates.enabled } : {}), ...updates } : tt
      );
      taskTypes.manuallySetData(optimistic);
    }
    await sendData({
      url: `/task-types/${id}`,
      method: 'patch',
      data: {
        data: { id, type: 'task-types', attributes: toApiAttributes(updates) },
      },
    });
    destroyCache(['task-types']);
    await taskTypes.update();
  }, [taskTypes]);

  return {
    data,
    enabledModeIds,
    loading: taskTypes.loading || appModes.loading,
    update: taskTypes.update,
    updateTaskType,
    setData: taskTypes.manuallySetData,
  };
};
